import { extractFieldNoteBody, FIELD_NOTE_BODY_PROP_LABEL } from '@civilgrid/utils/src/geoItems/fieldNoteBody';
import { DateTime } from 'luxon';
import { proxy } from 'valtio';
import { proxySet } from 'valtio/utils';
import { listGeoItems, printGeoItems } from '~/api/projects2';
import { notifyExportQueued } from '~/common/notifyExportQueued';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { GeoItem, User } from '~/common/types';
import { Notify } from '~/components2/Notify';
import rootStore from '~/store';

export type FieldNotesExportFileType = 'pdf' | 'zip';

const FIELD_NOTES_EXPORT_NOTIFY_MESSAGES: Record<FieldNotesExportFileType, string> = {
  pdf: 'Your export (PDF) has started. You will receive the file in your email in the next few minutes.',
  zip: 'Your export (zip) has started. You will receive the file in your email in the next few minutes.',
};

export interface FieldNote {
  id: string;
  type: 'image' | 'video' | 'note';
  files: {
    id: string;
    name: string;
    path: string;
    thumbnailPath: string;
    notes: string;
    type: string;
    compassHeading?: number | null;
  }[];
  /** Raw geo-item props (present at runtime via the API spread). Kept so the note
   * editor can preserve sibling props when rewriting the `Body` value. */
  props?: GeoItem['props'];
  /** Body text for `note`-type field notes (extracted from `props`). */
  body?: string;
  geometry: GeoJSON.Point;
  createdBy: User;
  createdAt: DateTime;
  updatedAt: DateTime;
  /** Server version, sent back as the edit's base version. */
  version?: number;
  /** Unified 1..N+M export number stamped by the jobs processor before
   * passing items to the headless renderer. Absent in the live UI. */
  i?: number;
}

export function transformFieldNote(note: any) {
  return {
    ...note,
    files: Array.isArray(note.files) ? note.files : [],
    body: note.type === 'note' ? extractFieldNoteBody(note.props) : undefined,
    createdAt: DateTime.fromISO(note.createdAt),
    updatedAt: DateTime.fromISO(note.updatedAt),
  };
}

export function transformFieldNotes(notes: any[]) {
  return notes
    .filter((note) => note.type === 'image' || note.type === 'video' || note.type === 'note')
    .map(transformFieldNote);
}

/**
 * Returns `existing` props with the field-note `Body` value set to `draft` — replacing
 * the current `Body` prop, or appending one if absent. Sibling props pass through
 * untouched. Exported as a pure helper so the note editor stays thin and testable.
 */
export function buildNoteBodyProps(existing: GeoItem['props'], draft: string): GeoItem['props'] {
  const hasBody = existing.some((p) => p?.label === FIELD_NOTE_BODY_PROP_LABEL);
  if (hasBody) {
    return existing.map((p) => (p?.label === FIELD_NOTE_BODY_PROP_LABEL ? { ...p, value: draft } : p));
  }
  return [...existing, { label: FIELD_NOTE_BODY_PROP_LABEL, value: draft, type: 'string' }];
}

/** `Notify.error` payload for a failed field-note edit or delete. */
export function fieldNoteMutationErrorNotify(operation: 'update' | 'delete') {
  return {
    title: 'Field Notes',
    message: `There was an error ${operation === 'update' ? 'updating' : 'deleting'} this field note, please contact support.`,
  };
}

/**
 * Parses a map feature's stringified `item` property into a FieldNote.
 * Returns `null` for missing/malformed payloads so the map click handler never throws.
 */
export function parseFieldNoteFeature(raw: unknown): FieldNote | null {
  if (typeof raw !== 'string') {
    return null;
  }
  try {
    return transformFieldNote(JSON.parse(raw)) as FieldNote;
  } catch {
    return null;
  }
}

// TODO(CG-117): project_field_note_added is reserved but mobile-only — wire it on the mobile app or backend.

class Store {
  selectedIds: Set<string> = proxySet();
  fieldNotes: FieldNote[] = [];
  hoveredItem: FieldNote | null = null;
  detailItem: FieldNote | null = null;
  /** A load for the current `projectId` has finished. False while loading and after a failure,
   * so a deep link is never reconciled against a list that is not the project's real contents. */
  fieldNotesLoaded = false;
  /** Active project id; held here so the module-scope map-click handler (no React
   * context) can attribute analytics events. Set by `fetchFieldNotes`. */
  projectId: string | null = null;

  async fetchFieldNotes(projectId: string) {
    this.projectId = projectId;
    this.fieldNotesLoaded = false;
    this.fieldNotes = [];
    this.selectedIds.clear();
    this.hoveredItem = null;
    this.detailItem = null;
    await this.refreshFieldNotes(projectId);
  }

  /** Re-fetches the project's field notes and replaces the list in place, without
   * clearing selection/hover/detail state. Used after an edit or delete so the grid
   * reflects the change. Stale results (a newer fetch changed `projectId`) are ignored. */
  async refreshFieldNotes(projectId: string) {
    const requestedId = projectId;
    try {
      const items = await listGeoItems(projectId);
      if (this.projectId !== requestedId) return;
      this.fieldNotes = transformFieldNotes(items);
      this.fieldNotesLoaded = true;
    } catch (e) {
      if (this.projectId !== requestedId) return;
      // Surface to the user and render an empty state rather than hanging or
      // producing an unhandled rejection in the caller's effect.
      console.error('Error loading field notes', e);
      this.fieldNotes = [];
      this.fieldNotesLoaded = false;
      Notify.error({
        title: 'Field Notes',
        message: 'There was an error loading field notes, please contact support.',
      });
    }
  }

  setHoveredItem(fieldNote: FieldNote | null) {
    this.hoveredItem = fieldNote;
  }

  setDetailItem(fieldNote: FieldNote | null) {
    this.detailItem = fieldNote;
  }

  /** Opens a field note in the detail viewer and records the interaction.
   * `source` distinguishes the grid (card/thumbnail click), the map marker, and arrival
   * via a `?geoItem=` link such as the one the CAD export carries. */
  openDetailItem(fieldNote: FieldNote, source: 'grid' | 'map' | 'deeplink') {
    if (this.projectId) {
      captureTypedPosthogEvent(POSTHOG_EVENTS.projectFieldNoteOpened, {
        project_id: this.projectId,
        organization_id: rootStore.currentOrganizationId || undefined,
        note_id: fieldNote.id,
        note_type: fieldNote.type,
        source,
      });
    }
    this.detailItem = fieldNote;
  }

  clearSelectedIds() {
    this.selectedIds.clear();
  }

  /** Queues a PDF/zip export of the selected field notes (delivered by email) and
   * records the interaction via PostHog. */
  async exportFieldNotes(fileType: FieldNotesExportFileType, ccEmails?: string[]) {
    const projectId = this.projectId;
    if (!projectId) return;
    const ids = Array.from(this.selectedIds);
    try {
      await printGeoItems(projectId, ids, 'printImageExhibit', fileType, ccEmails);
    } catch (e) {
      console.error('Error exporting field notes', e);
      Notify.error({
        title: 'Field Notes',
        message: 'An error occurred while starting your export, please contact support.',
      });
      return false;
    }
    try {
      captureTypedPosthogEvent(POSTHOG_EVENTS.projectFieldNotesExported, {
        project_id: projectId,
        organization_id: rootStore.currentOrganizationId || undefined,
        file_type: fileType,
        item_count: ids.length,
      });
    } catch (e) {
      console.error('Error capturing field notes export event', e);
    }
    notifyExportQueued({ message: FIELD_NOTES_EXPORT_NOTIFY_MESSAGES[fileType], ccEmails });
  }
}

const store = proxy(new Store());

export default store;
